import React from 'react';
import { format, parse } from 'date-fns';
import { theme } from '@policygenius/mortar';
import { ApolloError } from '@apollo/client';
import { CaseCommunicationsQuery } from 'query/caseCommunications/query';
import { CaseCommunicationType } from 'graph/types';
import { EmailIcon, SmsIcon, PhoneIcon } from './icons';
import {
  Tab,
  Content,
  Description,
  Row,
  Indicator,
  RowLabel,
  RowDate,
  EmptyState,
  ErrorState,
} from './styles';

export const ADVISOR_MESSAGING =
  'This communication history provides visibility into client outreach completed by the ' +
  'Policygenius team, including email, text, and phone call. This is not a complete record of ' +
  'all client interactions. For additional details, please contact us via chat or email at ' +
  'pgproaccounts@policygenius.com.';

export const ERROR_MESSAGE =
  'Something went wrong. Please contact pgproaccounts@policygenius.com for assistance.';

const COMMUNICATION_DISPLAY: Record<
  CaseCommunicationType,
  { label: string; icon: React.ReactNode }
> = {
  [CaseCommunicationType.Email]: {
    label: 'Email',
    icon: <EmailIcon label="Email" color={theme.colors.orange300} />,
  },
  [CaseCommunicationType.Sms]: {
    label: 'SMS',
    icon: <SmsIcon label="SMS" color={theme.colors.green300} />,
  },
  [CaseCommunicationType.Phone]: {
    label: 'Phone',
    icon: <PhoneIcon label="Phone" color={theme.colors.blue300} />,
  },
};

const formatDisplayDate = (date: string) =>
  format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy');

const groupByDate = (communications: CaseCommunicationsQuery['caseCommunications']) => {
  const dates: string[] = [];
  const typesByDate = new Map<string, CaseCommunicationType[]>();

  communications.forEach(({ date, type }) => {
    const types = typesByDate.get(date);

    if (!types) {
      dates.push(date);
      typesByDate.set(date, [type]);
    } else if (!types.includes(type)) {
      types.push(type);
    }
  });

  return dates.map((date) => ({ date, types: typesByDate.get(date) as CaseCommunicationType[] }));
};

export const CommunicationsTab: React.FC<{
  communications: CaseCommunicationsQuery['caseCommunications'];
  loading?: boolean;
  error?: ApolloError;
}> = ({ communications, loading, error }) => {
  // Surface backend failures instead of masking them as an empty history — an advisor
  // must be able to tell "no outreach happened" from "the request failed".
  if (error) {
    return (
      <Tab>
        <Content>
          <ErrorState color="secondary">{ERROR_MESSAGE}</ErrorState>
        </Content>
      </Tab>
    );
  }

  return (
    <Tab>
      <Content>
        <Description color="secondary" size="small">
          {ADVISOR_MESSAGING}
        </Description>
        {/* Suppress the empty state while loading so it doesn't briefly imply "no communications". */}
        {loading ? null : communications.length === 0 ? (
          <EmptyState color="secondary">There are no communications to display.</EmptyState>
        ) : (
          groupByDate(communications).map(({ date, types }) => (
            <Row key={date}>
              {types.map((type) => {
                const { label, icon } = COMMUNICATION_DISPLAY[type];

                return (
                  <Indicator key={type}>
                    {icon}
                    <RowLabel weight="bold">{label}</RowLabel>
                  </Indicator>
                );
              })}
              <RowDate color="secondary">{formatDisplayDate(date)}</RowDate>
            </Row>
          ))
        )}
      </Content>
    </Tab>
  );
};
